// Lightweight loader for Google Maps JavaScript API (Places library)
// Expects Vite env: VITE_GOOGLE_MAPS_API_KEY or VITE_GOOGLE_API_KEY

let loadPromise: Promise<typeof google> | null = null;

export function loadGoogleMapsPlaces(): Promise<typeof google> {
    if (typeof window !== 'undefined' && (window as any).google?.maps?.places) {
        return Promise.resolve((window as any).google as typeof google);
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        const apiKey = (import.meta as any)?.env?.VITE_GOOGLE_MAPS_API_KEY || (import.meta as any)?.env?.VITE_GOOGLE_API_KEY;
        if (!apiKey) {
            // Allow app to continue without Places
            reject(new Error('Google Maps API key not found (VITE_GOOGLE_MAPS_API_KEY or VITE_GOOGLE_API_KEY).'));
            return;
        }
        const cbName = '__onGoogleMapsPlacesLoaded__' + Math.random().toString(36).slice(2);
        (window as any)[cbName] = () => {
            delete (window as any)[cbName];
            resolve((window as any).google as typeof google);
        };
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly&callback=${cbName}`;
        script.async = true;
        script.onerror = () => reject(new Error('Failed to load Google Maps JS API'));
        document.head.appendChild(script);
    });

    return loadPromise;
}

export function shortAddressLabel(
    name?: string | null,
    city?: string | null,
    state?: string | null,
    country?: string | null
): string {
    const parts: string[] = [];
    if (name) parts.push(name);
    const loc = [city || undefined, state || (!state && country ? country : undefined)]
        .filter(Boolean)
        .join(', ');
    if (loc) parts.push(loc);
    return parts.join(', ');
}

export function extractAddressParts(components: google.maps.GeocoderAddressComponent[] | undefined): {
    city?: string | null;
    state?: string | null;
    country?: string | null;
} {
    const get = (types: string[]) => components?.find(c => types.every(t => c.types.includes(t)));
    const city = get(['locality'])?.long_name || get(['postal_town'])?.long_name || null;
    const state = get(['administrative_area_level_1'])?.short_name || null;
    const country = get(['country'])?.short_name || null;
    return { city, state, country };
}
